<script setup lang="ts">
import { storeToRefs } from "pinia";
import { defineComponent, h, ref } from "vue";

import Button from "../../../Reusables/Buttons/Button.vue";
import IconButton from "../../../Reusables/Buttons/IconButton.vue";
import DropDown from "../../../Reusables/Inputs/DropDown.vue";
import Checkbox from "../../../Reusables/Inputs/CheckBox.vue";
import FileUpload from "../../../Reusables/Inputs/FileUpload.vue";
import { useToast } from "vue-toastification";
import { getExcelTemplate, importMaterialsFromExcel } from "../../../../api/services/Mapping";
import { BuildingProjectType } from "@/features/BuildingProject";
import { ExcelImportResponse } from "../../../../types/Views/MappingWizard/ExcelImportResponse";
import ExcelIcon from "../../../../assets/Images/ExcelIcon.png";
import ImportErrorsModal from "./ImportErrorsModal.vue";
import InputLabel from "@/components/Reusables/Inputs/InputLabel.vue";

import { useProject } from "@/stores/project/project.store";
import { useModalStore } from "@/stores/modals/modals.store";
import { useDashboard } from "@/stores/dashboard/dashboard.store";
import { useProjectEditReadOnly } from "@/pages/ProjectVersionHistory/composables/useProjectEditReadOnly.composable";
import RevisionGatedButton from "@/pages/ProjectVersionHistory/components/revisions/RevisionGatedButton.vue";

const modals = useModalStore();

// The toolbar's Upload trigger is already inert for a Viewer and while previewing; this is the
// second layer, for a modal left open across a role or revision change (RTLCA-5253, RTLCA-5582).
const { blockedByPreview } = useProjectEditReadOnly();

const dashboardStore = useDashboard();
const { fetchDashboard } = dashboardStore;

const projectStore = useProject();
const { fetchProjectMaterials } = projectStore;
// Every model, not just the Speckle branches: an Excel import only writes a branch name onto the
// materials it creates, so a model pushed from APS/Forma is a valid target too (RTLCA-5291).
const { selectedModel, allModelNames } = storeToRefs(projectStore);

const props = defineProps<{
  project: BuildingProjectType;
}>();

const currentBranchName = ref<string>(selectedModel.value ?? "");
const importErrorsModalIsHidden = ref<boolean>(true);
const isImporting = ref<boolean>(false);
const hasError = ref<boolean>(false);
const overwriteExisting = ref<boolean>(true);

const uploadResponse = ref<ExcelImportResponse>();
const selectedFile = ref<File | null>(null);

const onFilesUpdate = (files: File[]) => {
  selectedFile.value = files[0] ?? null;
  uploadResponse.value = undefined;
  hasError.value = false;
};

const onFileError = () => {
  useToast().error("Please use .xlsx files");
};

const handleImportMaterials = async () => {
  // The upload menu is already closed while a frozen revision is previewed; this covers a modal that
  // was already open when the revision changed — the import would land on the current revision.
  if (blockedByPreview()) return;
  if (!selectedFile.value) return;

  isImporting.value = true;
  hasError.value = false;

  try {
    uploadResponse.value = await importMaterialsFromExcel(
      props.project.id,
      currentBranchName.value,
      selectedFile.value,
      overwriteExisting.value
    );
  } catch (_err) {
    useToast().error("Something went wrong with your upload");
    hasError.value = true;
    isImporting.value = false;
  }

  if (!uploadResponse.value) {
    isImporting.value = false;
    return;
  }
  const rowErrors = uploadResponse.value.importRowErrors;
  if (rowErrors.length > 0) {
    const shown = rowErrors.slice(0, 5);
    const extra = rowErrors.length - shown.length;
    useToast().error(
      defineComponent({
        render: () =>
          h("div", [
            h(
              "p",
              { class: "t-small-bold" },
              `Import failed — ${rowErrors.length} row error${rowErrors.length !== 1 ? "s" : ""}`
            ),
            h("ul", { class: "mt-px-4 flex flex-col gap-px-2 list-disc pl-px-16" }, [
              ...shown.map((e) => h("li", { class: "t-xs" }, `Row ${e.rowNumber}: ${e.reason}`)),
              ...(extra > 0 ? [h("li", { class: "t-xs text-type-muted" }, `…and ${extra} more`)] : []),
            ]),
          ]),
      })
    );
  } else if (uploadResponse.value.numberOfImportErrors > 0) {
    useToast().error(`${uploadResponse.value.numberOfImportErrors} errors were found in the imported file`);
  }
  if (uploadResponse.value.numberOfMaterialsImported > 0) {
    useToast().success(`${uploadResponse.value.numberOfMaterialsImported} materials was imported to ${currentBranchName.value}`);
  }

  fetchDashboard();
  await fetchProjectMaterials();
  isImporting.value = false;

  if (uploadResponse.value.numberOfImportErrors === 0 && rowErrors.length === 0) {
    modals.close();
  }
};

const handleDownloadTemplate = async () => {
  const resp = await getExcelTemplate();

  const blob = new Blob([resp], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });

  const objectURL = URL.createObjectURL(blob);
  let a = document.createElement("a");
  a.href = objectURL;
  a.download = `materials-template.xlsx`;
  a.click();

  useToast().success("Template Downloaded");
};

const toggleViewimportErrors = () => {
  importErrorsModalIsHidden.value = !importErrorsModalIsHidden.value;
};

const toggleOverwriteExisting = () => {
  overwriteExisting.value = !overwriteExisting.value;
  // TODO keep this in local storage?
};
</script>

<template>
  <div class="flex items-center justify-between">
    <h4 class="t-h4 text-type-default">Upload Excel file</h4>
  </div>
  <div class="text-text-color flex w-fit flex-col gap-8">
    <span>Upload materials or types from an Excel file</span>

    <div class="flex items-end justify-between gap-x-2">
      <div class="flex flex-col">
        <InputLabel
          id="download-template-btn"
          label="Please use this template" />
        <IconButton
          id="download-template-btn"
          icon="fa-solid fa-download"
          color="primary"
          label="Download Template"
          @click="handleDownloadTemplate" />
      </div>

      <DropDown
        :key="currentBranchName"
        v-model:value="currentBranchName"
        color="primary"
        label="Upload to model"
        title="Select model"
        :options="allModelNames"
        data-cy="branch-select" />
    </div>

    <div>
      <Checkbox
        :key="+overwriteExisting"
        :value="overwriteExisting"
        color="primary"
        size="xl"
        label="Overwrite existing materials"
        @update:value="toggleOverwriteExisting" />
    </div>

    <FileUpload
      data-cy="excel-drop-zone"
      accept=".xlsx"
      :error-msg="(uploadResponse?.importRowErrors.length ?? 0) > 0 || hasError ? 'Import failed — see errors below' : undefined"
      @update:files="onFilesUpdate"
      @error="onFileError">
      <template #preview>
        <img
          class="w-1/2"
          :src="ExcelIcon"
          alt="Excel file" />
      </template>
    </FileUpload>

    <!-- File errors -->
    <div
      v-if="uploadResponse && uploadResponse.importRowErrors.length > 0"
      class="text-text-color flex flex-col gap-4 wrap-break-word">
      <div class="flex flex-col">
        <span>Woops... it appears that there was an issue with the file you are trying to import.</span>
        <span>Please click the button below to further investigate and identify the error.</span>
      </div>

      <div>
        <Button
          label="Inspect Errors"
          color="inline"
          @click="toggleViewimportErrors" />
      </div>
    </div>

    <div class="flex w-full justify-between">
      <Button
        color="button"
        label="Cancel"
        @click="modals.close()" />
      <RevisionGatedButton
        color="primary"
        :is-loading="isImporting"
        :label="isImporting ? 'Importing...' : 'Import'"
        data-cy="import-excel-btn"
        :guard="useProjectEditReadOnly"
        @click="handleImportMaterials" />
    </div>
  </div>

  <div
    v-if="!importErrorsModalIsHidden && uploadResponse"
    class="relative h-screen w-screen">
    <ImportErrorsModal
      :upload-response="uploadResponse"
      @toggle-modal="toggleViewimportErrors" />
  </div>
</template>
